import React, { useState, useEffect, useRef, useCallback } from 'react';
import Layout from './Layout';
import { useTheme } from '../contexts/ThemeContext';
import {
  Tv2, Plus, Trash2, Edit2, Clock, Play, Pause,
  Upload, X, Film, CheckCircle2, AlertCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';

const MAX_FILE_BYTES = 500 * 1024 * 1024;
const ALLOWED_EXT   = /\.(mp4|mov|avi|mkv|webm)$/i;

const formatDuration = (s) => {
  if (!s) return '00:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};
const formatSize = (bytes) => {
  if (!bytes) return '0 MB';
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
};

const INTERVAL_OPTIONS = [
  { value: 2,  label: 'Cada 2 videos' },
  { value: 4,  label: 'Cada 4 videos' },
  { value: 8,  label: 'Cada 8 videos' },
  { value: 10, label: 'Cada 10 videos' },
];

// ── Main Component ──────────────────────────────────────────────────────────────
function Avances({ user, onLogout }) {
  const { isDark } = useTheme();
  const [avances, setAvances]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [showModal, setShowModal]     = useState(false);
  const [editingAvance, setEditingAvance] = useState(null);
  const [uploading, setUploading]     = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragOver, setDragOver]       = useState(false);
  const [selectedFile, setSelectedFile] = useState(null); // { file, status, progress }

  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name:            '',
    description:     '',
    interval_videos: 4,
    is_active:       true
  });

  useEffect(() => { fetchAvances(); }, []);

  const fetchAvances = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/api/avances', { headers: { Authorization: `Bearer ${token}` } });
      setAvances(res.data.avances || []);
    } catch {
      toast.error('Error al cargar avances');
    } finally {
      setLoading(false);
    }
  };

  // ── File handling ──────────────────────────────────────────────────────────
  const setFile = useCallback((file) => {
    if (!file) return;
    if (!file.type.startsWith('video/') && !ALLOWED_EXT.test(file.name)) {
      toast.error(`${file.name}: formato no soportado`); return;
    }
    if (file.size > MAX_FILE_BYTES) {
      toast.error(`El archivo supera los ${formatSize(MAX_FILE_BYTES)}`); return;
    }
    setSelectedFile({ file, status: 'pending', progress: 0 });
  }, []);

  const handleDragOver  = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = ()  => setDragOver(false);
  const handleDrop      = (e) => { e.preventDefault(); setDragOver(false); setFile(e.dataTransfer.files[0]); };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile && !editingAvance) {
      toast.error('Selecciona un archivo de video'); return;
    }

    const token = localStorage.getItem('token');
    const fd    = new FormData();
    fd.append('name',            formData.name);
    fd.append('description',     formData.description);
    fd.append('interval_videos', formData.interval_videos);
    fd.append('is_active',       formData.is_active);
    if (selectedFile) fd.append('video', selectedFile.file);

    setUploading(true);
    setUploadProgress(0);
    if (selectedFile) setSelectedFile(prev => ({ ...prev, status: 'uploading' }));

    try {
      const onProgress = (e) => {
        const pct = Math.round((e.loaded * 100) / e.total);
        setUploadProgress(pct);
        if (selectedFile) setSelectedFile(prev => ({ ...prev, progress: pct }));
      };
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' };

      if (editingAvance) {
        await axios.put(`/api/avances/${editingAvance.id}`, fd, { headers, onUploadProgress: onProgress });
        toast.success('Avance actualizado correctamente');
      } else {
        await axios.post('/api/avances', fd, { headers, onUploadProgress: onProgress });
        toast.success('Avance creado correctamente');
      }

      if (selectedFile) setSelectedFile(prev => ({ ...prev, status: 'done', progress: 100 }));
      setTimeout(() => { closeModal(); fetchAvances(); }, 600);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al guardar avance');
      if (selectedFile) setSelectedFile(prev => ({ ...prev, status: 'error' }));
    } finally {
      setUploading(false);
    }
  };

  // ── Delete / toggle ────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este avance de TV?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/avances/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Avance eliminado');
      fetchAvances();
    } catch { toast.error('Error al eliminar avance'); }
  };

  const handleToggleActive = async (avance) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/avances/${avance.id}/toggle`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(avance.is_active ? 'Avance desactivado' : 'Avance activado');
      fetchAvances();
    } catch { toast.error('Error al cambiar estado'); }
  };

  // ── Modal helpers ──────────────────────────────────────────────────────────
  const openCreateModal = () => {
    setEditingAvance(null);
    setFormData({ name: '', description: '', interval_videos: 4, is_active: true });
    setSelectedFile(null);
    setUploadProgress(0);
    setShowModal(true);
  };

  const openEditModal = (avance) => {
    setEditingAvance(avance);
    setFormData({
      name:            avance.name,
      description:     avance.description || '',
      interval_videos: avance.interval_videos || 4,
      is_active:       avance.is_active
    });
    setSelectedFile(null);
    setUploadProgress(0);
    setShowModal(true);
  };

  const closeModal = () => {
    if (uploading) return;
    setShowModal(false);
    setEditingAvance(null);
    setSelectedFile(null);
    setUploadProgress(0);
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex justify-center items-center h-screen">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-accent-purple/30 border-t-accent-purple rounded-full animate-spin"></div>
          </div>
        </div>
      </Layout>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Layout user={user} onLogout={onLogout}>
      <style>{`
        @keyframes avSpin { to { transform: rotate(360deg); } }
        .av-input {
          width: 100%; padding: 13px 16px;
          background: ${isDark ? '#1a1e2e' : '#ffffff'};
          border: 1px solid ${isDark ? '#252837' : 'rgba(0,0,0,0.12)'};
          border-radius: 10px; color: ${isDark ? '#f3f4f6' : '#111827'};
          font-size: 14px; outline: none; box-sizing: border-box;
          font-family: inherit; transition: border-color 0.2s, box-shadow 0.2s;
        }
        .av-input:focus { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99,102,241,0.18); }
        .av-input::placeholder { color: ${isDark ? '#4b5563' : 'rgba(0,0,0,0.35)'}; }
        .av-select { appearance: none; cursor: pointer; }
        .av-btn-primary {
          flex: 1; padding: 13px 20px;
          background: linear-gradient(135deg, #6366f1 0%, #7c3aed 100%);
          border: none; border-radius: 10px; color: white;
          font-size: 14px; font-weight: 600; cursor: pointer;
          display: flex; align-items: center; justify-content: center; gap: 8px;
          transition: opacity 0.2s; font-family: inherit;
        }
        .av-btn-primary:hover:not(:disabled) { opacity: 0.9; }
        .av-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
        .av-btn-cancel {
          padding: 13px 24px;
          background: ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'};
          border: 1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'};
          border-radius: 10px; color: ${isDark ? '#9ca3af' : '#6B7280'};
          font-size: 14px; font-weight: 500; cursor: pointer;
          transition: background 0.2s, color 0.2s; font-family: inherit;
        }
        .av-btn-cancel:hover {
          background: ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'};
          color: ${isDark ? 'white' : '#111827'};
        }
        .av-dropzone {
          border: 2px dashed ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'};
          border-radius: 12px; padding: 28px 20px; text-align: center; cursor: pointer;
          transition: border-color 0.2s, background 0.2s;
          background: ${isDark ? 'rgba(26,31,46,0.6)' : 'rgba(0,0,0,0.03)'};
        }
        .av-dropzone:hover, .av-dropzone.dragover {
          border-color: rgba(99,102,241,0.6);
          background: rgba(99,102,241,0.06);
        }
        .av-spinner {
          width: 18px; height: 18px;
          border: 2px solid rgba(255,255,255,0.25);
          border-top-color: white; border-radius: 50%;
          animation: avSpin 0.8s linear infinite;
        }
      `}</style>

      <div className="p-2.5 min-h-screen max-w-[1600px] mx-auto">

        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-10">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-1">Avances de TV</h1>
            <p className="text-gray-400 text-sm">
              Inserta un video promocional cada N videos en tus playlists automáticamente.
            </p>
          </div>
          <button
            className="btn-primary-gradient flex-shrink-0"
            onClick={openCreateModal}
          >
            <Plus size={18} /> Agregar Avance
          </button>
        </div>

        {/* Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {avances.map((avance) => (
            <div key={avance.id} className="rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#1a1f2e] p-6 transition-all duration-300 shadow-sm dark:shadow-none">
              {/* Card header */}
              <div className="flex justify-between items-start mb-5">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-12 h-12 rounded-2xl bg-sky-500/15 border border-sky-500/30 flex items-center justify-center flex-shrink-0">
                    <Tv2 className="text-sky-400" size={22} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-bold text-gray-900 dark:text-white truncate">{avance.name}</h3>
                    <p className="text-xs text-gray-500 truncate">{avance.description || 'Sin descripción'}</p>
                  </div>
                </div>
                <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold border flex-shrink-0 ml-2 ${
                  avance.is_active
                    ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                    : 'bg-gray-500/15 text-gray-400 border-gray-500/30'
                }`}>
                  {avance.is_active ? 'Activo' : 'Inactivo'}
                </span>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-2 mb-4">
                <div className="bg-black/5 dark:bg-black/30 rounded-xl p-2.5 border border-black/8 dark:border-white/10">
                  <div className="text-[10px] text-gray-400 dark:text-gray-500 mb-1 font-semibold tracking-wider uppercase">Duración</div>
                  <div className="text-gray-900 dark:text-white text-sm font-semibold flex items-center gap-1">
                    <Clock size={11} className="text-sky-400" />
                    {formatDuration(avance.duration)}
                  </div>
                </div>
                <div className="bg-black/5 dark:bg-black/30 rounded-xl p-2.5 border border-black/8 dark:border-white/10">
                  <div className="text-[10px] text-gray-400 dark:text-gray-500 mb-1 font-semibold tracking-wider uppercase">Intervalo</div>
                  <div className="text-gray-900 dark:text-white text-sm font-semibold">c/ {avance.interval_videos || 4} vids</div>
                </div>
              </div>

              {/* Video name badge */}
              <div className="flex items-center gap-2 mb-5">
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-black/5 dark:bg-black/30 border border-black/8 dark:border-white/10 min-w-0 overflow-hidden">
                  <Film size={11} className="text-gray-400 flex-shrink-0" />
                  <span className="text-xs text-gray-600 dark:text-gray-300 font-medium truncate">
                    {avance.original_name || avance.file_path?.split('/').pop() || '1 video'}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all duration-200 ${
                    avance.is_active
                      ? 'bg-amber-500/15 hover:bg-amber-500/25 text-amber-600 dark:text-amber-400 border border-amber-500/30'
                      : 'bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30'
                  }`}
                  onClick={() => handleToggleActive(avance)}
                >
                  {avance.is_active ? <Pause size={14} /> : <Play size={14} />}
                  {avance.is_active ? 'Desactivar' : 'Activar'}
                </button>
                <button
                  className="w-10 h-10 flex items-center justify-center rounded-xl bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-black/10 dark:border-white/10 transition-all duration-200"
                  onClick={() => openEditModal(avance)}
                >
                  <Edit2 size={15} />
                </button>
                <button
                  className="w-10 h-10 flex items-center justify-center rounded-xl bg-black/5 dark:bg-white/5 hover:bg-red-500/15 dark:hover:bg-red-600/20 text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 border border-black/10 dark:border-white/10 hover:border-red-500/30 dark:hover:border-red-600/30 transition-all duration-200"
                  onClick={() => handleDelete(avance.id)}
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}

          {/* Add card */}
          <div
            className="rounded-2xl border-2 border-dashed border-white/15 hover:border-sky-500/40 p-6 flex flex-col items-center justify-center cursor-pointer min-h-[280px] group transition-all duration-300 hover:bg-sky-500/5"
            onClick={openCreateModal}
          >
            <div className="w-16 h-16 rounded-full border-2 border-white/20 group-hover:border-sky-500/50 flex items-center justify-center mb-5 transition-all duration-300">
              <Plus size={28} className="text-gray-400 group-hover:text-sky-400 transition-colors" />
            </div>
            <h3 className="text-base font-semibold text-gray-400 group-hover:text-gray-200 transition-colors mb-2">
              Agregar Nuevo Avance
            </h3>
            <p className="text-xs text-gray-600 text-center leading-relaxed">
              Un video promocional que se inserta automáticamente en tus playlists
            </p>
          </div>
        </div>

        {/* ── MODAL ── */}
        {showModal && (
          <div className="modal-overlay-premium animate-fadeIn" onClick={closeModal}>
            <div
              className="modal-premium animate-scale-in"
              style={{ maxWidth: '520px', width: '100%' }}
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-3">
                  <div style={{
                    width: '38px', height: '38px', borderRadius: '10px',
                    background: 'linear-gradient(135deg, rgba(14,165,233,0.3), rgba(99,102,241,0.3))',
                    border: '1px solid rgba(14,165,233,0.4)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <Tv2 size={18} style={{ color: '#38bdf8' }} />
                  </div>
                  <h2 style={{ color: isDark ? 'white' : '#111827', fontSize: '20px', fontWeight: 700, margin: 0 }}>
                    {editingAvance ? 'Editar Avance' : 'Nuevo Avance de TV'}
                  </h2>
                </div>
                <button onClick={closeModal} disabled={uploading}
                  style={{ background: 'none', border: 'none', cursor: uploading ? 'not-allowed' : 'pointer',
                           color: '#6b7280', padding: '6px', borderRadius: '8px', display: 'flex', alignItems: 'center' }}>
                  <X size={22} />
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                {/* Name */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: isDark ? '#e5e7eb' : '#374151', fontSize: '13px',
                                  fontWeight: 600, marginBottom: '7px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Nombre del Avance
                  </label>
                  <input type="text" className="av-input"
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ej: Avance Noticiero Nocturno"
                    required disabled={uploading} />
                </div>

                {/* Description */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: isDark ? '#e5e7eb' : '#374151', fontSize: '13px',
                                  fontWeight: 600, marginBottom: '7px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Descripción
                  </label>
                  <textarea className="av-input" rows={2}
                    value={formData.description}
                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Descripción del avance (opcional)"
                    disabled={uploading} style={{ resize: 'none' }} />
                </div>

                {/* Interval */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: isDark ? '#e5e7eb' : '#374151', fontSize: '13px',
                                  fontWeight: 600, marginBottom: '7px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Intervalo de Reproducción
                  </label>
                  <div style={{ position: 'relative' }}>
                    <select className="av-input av-select"
                      value={formData.interval_videos}
                      onChange={e => setFormData({ ...formData, interval_videos: parseInt(e.target.value) })}
                      disabled={uploading} style={{ paddingRight: '36px' }}>
                      {INTERVAL_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                    <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
                                  pointerEvents: 'none', color: '#6b7280' }}>▾</div>
                  </div>
                </div>

                {/* Video upload */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: isDark ? '#e5e7eb' : '#374151', fontSize: '13px',
                                  fontWeight: 600, marginBottom: '7px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {editingAvance ? 'Reemplazar Video (opcional)' : 'Video del Avance'}
                  </label>

                  {/* Current video info when editing */}
                  {editingAvance && !selectedFile && (
                    <div style={{
                      padding: '10px 12px', borderRadius: '10px', marginBottom: '10px',
                      background: 'rgba(14,165,233,0.08)', border: '1px solid rgba(14,165,233,0.25)',
                      display: 'flex', alignItems: 'center', gap: '8px'
                    }}>
                      <Film size={14} style={{ color: '#38bdf8', flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ color: '#7dd3fc', fontSize: '12px', fontWeight: 500, margin: 0,
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {editingAvance.original_name || editingAvance.file_path?.split('/').pop() || 'Video actual'}
                        </p>
                        <p style={{ color: '#6b7280', fontSize: '11px', margin: '2px 0 0' }}>
                          {formatDuration(editingAvance.duration)} · guardado
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Selected new file */}
                  {selectedFile && (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '10px 12px',
                      background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                      borderRadius: '10px',
                      border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
                      marginBottom: '10px'
                    }}>
                      <Film size={16} style={{ color: '#818cf8', flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ color: isDark ? '#f3f4f6' : '#111827', fontSize: '13px', fontWeight: 500,
                                    margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {selectedFile.file.name}
                        </p>
                        <p style={{ color: '#6b7280', fontSize: '11px', margin: '2px 0 0' }}>
                          {formatSize(selectedFile.file.size)}
                        </p>
                      </div>
                      {uploading && selectedFile.status === 'uploading' && (
                        <div style={{ width: '70px' }}>
                          <div style={{ height: '4px', background: 'rgba(255,255,255,0.08)', borderRadius: '2px', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${selectedFile.progress}%`,
                                          background: 'linear-gradient(90deg, #6366f1, #7c3aed)', borderRadius: '2px', transition: 'width 0.3s' }} />
                          </div>
                          <p style={{ color: '#818cf8', fontSize: '10px', textAlign: 'right', margin: '2px 0 0' }}>
                            {selectedFile.progress}%
                          </p>
                        </div>
                      )}
                      {selectedFile.status === 'done' && <CheckCircle2 size={16} style={{ color: '#34d399', flexShrink: 0 }} />}
                      {selectedFile.status === 'error' && <AlertCircle size={16} style={{ color: '#f87171', flexShrink: 0 }} />}
                      {!uploading && (
                        <button type="button" onClick={() => setSelectedFile(null)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280',
                                   padding: '2px', display: 'flex', alignItems: 'center' }}
                          onMouseEnter={e => e.currentTarget.style.color = '#f87171'}
                          onMouseLeave={e => e.currentTarget.style.color = '#6b7280'}>
                          <X size={15} />
                        </button>
                      )}
                    </div>
                  )}

                  {/* Dropzone */}
                  {!selectedFile && !uploading && (
                    <div
                      className={`av-dropzone ${dragOver ? 'dragover' : ''}`}
                      onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <input ref={fileInputRef} type="file"
                        accept="video/mp4,video/quicktime,video/x-msvideo,video/x-matroska,video/webm,.mp4,.mov,.avi,.mkv,.webm"
                        style={{ display: 'none' }}
                        onChange={e => setFile(e.target.files[0])} />
                      <div style={{
                        width: '48px', height: '48px', borderRadius: '12px',
                        background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px'
                      }}>
                        <Upload size={22} style={{ color: '#38bdf8' }} />
                      </div>
                      <p style={{ color: '#e5e7eb', fontWeight: 600, fontSize: '14px', margin: '0 0 4px' }}>
                        {editingAvance ? 'Arrastra o haz clic para cambiar el video' : 'Arrastra el video o haz clic para seleccionar'}
                      </p>
                      <p style={{ color: '#6b7280', fontSize: '12px', margin: 0 }}>
                        MP4, MOV, AVI, MKV · Máx. {formatSize(MAX_FILE_BYTES)}
                      </p>
                    </div>
                  )}
                </div>

                {/* Upload progress */}
                {uploading && (
                  <div style={{
                    padding: '12px 14px', borderRadius: '10px', marginBottom: '16px',
                    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ color: '#9ca3af', fontSize: '13px' }}>Subiendo video…</span>
                      <span style={{ color: '#38bdf8', fontSize: '13px', fontWeight: 700 }}>{uploadProgress}%</span>
                    </div>
                    <div style={{ height: '6px', background: 'rgba(255,255,255,0.06)', borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{
                        height: '100%', width: `${uploadProgress}%`,
                        background: 'linear-gradient(90deg, #0ea5e9, #6366f1)',
                        borderRadius: '3px', transition: 'width 0.3s'
                      }} />
                    </div>
                  </div>
                )}

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: '10px', paddingTop: '4px' }}>
                  <button type="submit" className="av-btn-primary" disabled={uploading}>
                    {uploading
                      ? <><div className="av-spinner" /> Subiendo…</>
                      : <>{editingAvance ? <Edit2 size={16} /> : <Plus size={16} />} {editingAvance ? 'Guardar Cambios' : 'Crear Avance'}</>
                    }
                  </button>
                  <button type="button" className="av-btn-cancel" onClick={closeModal} disabled={uploading}>
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Avances;
